import { effect, inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, take } from 'rxjs';
import { FeatureFlagService } from '../services/feature-flag.service';
import { DesignVersionService } from '../services/design-version.service';

/**
 * Lets a redesign-only route match only when `DesignVersionService.isRedesign()` is true; otherwise it
 * sends the user to `home`, the legacy page for that area. Waits for `flagsLoaded` like `roleplayV2Guard`,
 * so a live `UIRedesign` answer can win over the boot hint before the page is chosen.
 */
export function redesignGuard(home: string): CanMatchFn {
  return () => {
    const featureFlagService = inject(FeatureFlagService);
    const designVersion = inject(DesignVersionService);
    const router = inject(Router);
    const decide = () => designVersion.isRedesign() || router.parseUrl(home);

    // After boot the flags are always loaded. `toObservable` leaves an effect behind until the route's injector
    // is destroyed, so it is only for the navigations that run before the payload lands.
    if (featureFlagService.flagsLoaded()) return decide();
    return toObservable(featureFlagService.flagsLoaded).pipe(filter(Boolean), take(1), map(decide));
  };
}

/**
 * For a redesign-only page: the guard only runs on navigation, so when `UIRedesign` turns off while the
 * page is open, this leaves for `home`. Call it in the component's injection context.
 */
export function leaveWhenRedesignOff(home: string): void {
  const designVersion = inject(DesignVersionService);
  const router = inject(Router);

  effect(() => {
    if (!designVersion.isRedesign()) void router.navigateByUrl(home);
  });
}
